import {
  Box,
  Dialog,
  Grid,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import CenteredTextBox from "features/programmesActions/CenteredTextBox";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { mixpanel } from "common/analytics";
import FileTabs, { FileTabValue } from "common/components/FileTabs";
import { DarkGreenButton } from "features/DarkGreenButton";
import {
  AccessibleUser,
  createFileEntry,
  FileEntry,
  FileUpload,
  getAllFileEntriesForUser,
  selectAllAccessibleUsers,
  selectAllFileEntries,
  uploadFile,
  useAppDispatch,
  useAppSelector,
} from "model";
import { drawerWidth } from "width";
import AddNewFileDialog from "./AddNewFileDialog";
import CollapsibleFileTable from "./CollapsibleFileTable";
import { convertFromFormInput, FileEntryFormData } from "./FileEntryFormData";
import FileTable from "./FileTable";

/**
 * Main content area component for file management
 * Displays in the main area (where map is) when /file-management-overview route is active
 */
export default function FileManagement() {
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { notify } = useNotification();
  const [activeTab, setActiveTab] = useState<FileTabValue>("all");
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const fileEntriesLoading = useAppSelector(
    (state) => state.fileEntries.loading
  );

  const allFileEntries: Array<FileEntry> = useAppSelector((state) =>
    selectAllFileEntries(state)
  );

  const allAccessibleUsers: Array<AccessibleUser> = useAppSelector((state) =>
    selectAllAccessibleUsers(state)
  );

  const user = useAppSelector((state) => state.user.details);

  // Load all files on component mount
  useEffect(() => {
    if (user?.guid) {
      // Load all file entries (both uploaded by and for the current user)
      void dispatch(getAllFileEntriesForUser({ associatedUserId: undefined }));
    }
  }, [dispatch, user?.guid]);

  // Check if navigation came from push notification and refresh data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("fromPushNotification") === "true") {
      // Refresh file entries data - load all files the user has access to
      void dispatch(getAllFileEntriesForUser({ associatedUserId: undefined }));

      // Clear the query parameter to avoid repeated refreshes
      searchParams.delete("fromPushNotification");
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      history.replace(newUrl);
    }
  }, [location.search, dispatch, user?.guid, history, location.pathname]);

  // Filter files based on active tab
  const filteredFileEntries = allFileEntries.filter((fileEntry) => {
    switch (activeTab) {
      case "uploaded-by-me":
        return fileEntry.associatedUserId === user?.guid;
      case "uploaded-by-others":
        return fileEntry.associatedUserId !== user?.guid;
      case "all":
      default:
        return true;
    }
  });

  // Sort by creation date (newest first)
  const sortedFileEntries = [...filteredFileEntries].sort(
    (a: FileEntry, b: FileEntry) =>
      new Date(b.creationDate ?? "").valueOf() -
      new Date(a.creationDate ?? "").valueOf()
  );

  // Create accessible users map for quick lookup
  const allAccessibleUsersMap: { [guid: string]: AccessibleUser } = {};
  allAccessibleUsers.forEach((user) => {
    allAccessibleUsersMap[user.guid] = user;
  });

  // Bucket files by associatedUserId when showing "uploaded-by-others"
  type UserFileListMap = {
    [name: string]: Array<FileEntry>;
  };

  const bucketedFileEntries: UserFileListMap = {};
  if (activeTab === "uploaded-by-others") {
    sortedFileEntries.forEach((fileEntry) => {
      const associatedUserId = fileEntry.associatedUserId;
      if (!bucketedFileEntries[associatedUserId]) {
        bucketedFileEntries[associatedUserId] = [];
      }
      bucketedFileEntries[associatedUserId].push(fileEntry);
    });
  }

  const handleTabChange = (newTab: FileTabValue) => {
    setActiveTab(newTab);
    mixpanel.track("File tab changed", { tab: newTab });
  };

  const handleUploadClick = () => {
    mixpanel.track("Add new file clicked");
    setShowUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false);
  };

  const handleSaveFile = async (fileEntryFormData: FileEntryFormData) => {
    if (
      (!fileEntryFormData.file || !fileEntryFormData.fileName) &&
      !fileEntryFormData.fileUploadId
    ) {
      return;
    }

    if (!fileEntryFormData.fileUploadId) {
      if (!fileEntryFormData.file) {
        console.log("File content is missing, something went wrong");
        return;
      }
      const fileUpload = await dispatch(
        uploadFile({
          fileName: fileEntryFormData.fileName ?? "",
          file: fileEntryFormData.file,
        })
      );
      fileEntryFormData.fileUploadId = (fileUpload.payload as FileUpload).guid;
    }

    const fileEntry = convertFromFormInput(fileEntryFormData, user?.guid ?? "");

    await dispatch(createFileEntry(fileEntry));

    notify(t("fileEntry.add.success"));
    mixpanel.track("FileEntry created");

    setShowUploadDialog(false);

    // Refresh the file list - load all files the user has access to
    void dispatch(getAllFileEntriesForUser({ associatedUserId: undefined }));
  };

  if (fileEntriesLoading !== "idle") {
    return <LinearProgress />;
  }

  // On mobile, don't render this component - the drawer shows FileEntryDetail instead
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        zIndex: 1300,
        overflow: "hidden",
      }}
    >
      {/* Title */}
      <PageTitleBar title={t("go.to.file.management.button")} showDivider />

      {/* Description Text */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 3,
          py: 2,
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <CenteredTextBox
              text="Only users that upload a file for you will be able to see that file apart from you. Files that you upload for someone else will only be visible for you and the person you uploaded the file for."
              useMaxSpace={true}
              maxHeight="auto"
              lineHeight={20}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Filter and Actions Bar */}
      <Box
        sx={{
          padding: "0 16px 16px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {/* Tab Filters */}
        <FileTabs value={activeTab} onChange={handleTabChange} />

        {/* Action Buttons */}
        <DarkGreenButton
          title={"Upload new file"}
          handleUploadClick={handleUploadClick}
        />
      </Box>

      {/* File Table */}
      <Box
        sx={{
          flex: 1,
          padding: "0 16px 16px 16px",
          overflow: "auto",
          paddingBottom: isMobile ? "100px" : "16px", // Extra padding for mobile bottom nav
        }}
      >
        {activeTab === "uploaded-by-others" ? (
          <CollapsibleFileTable
            bucketedFileEntries={bucketedFileEntries}
            allAccessibleUsersMap={allAccessibleUsersMap}
            currentUserId={user?.guid}
          />
        ) : (
          <FileTable
            fileEntries={sortedFileEntries}
            currentUserId={user?.guid}
            allAccessibleUsersMap={allAccessibleUsersMap}
          />
        )}
      </Box>

      {/* Upload File Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            paddingTop: "24px",
          },
        }}
      >
        <AddNewFileDialog
          fileEntry={undefined}
          handleSave={handleSaveFile}
          handleClose={handleCloseUploadDialog}
        />
      </Dialog>
    </Box>
  );
}
